import { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useUsers, useRoles, useCompanies, useDeletedUsers, useFormattedUser } from '@adsk/alloy-provider';
import { useLocale } from '@adsk/alloy-react-locale';
import { ASSIGNEE_PICKER_COMPANY, ASSIGNEE_PICKER_ROLE, ASSIGNEE_PICKER_USER, DEFAULT_OPTIONS_ORDER, DEFAULT_USERS_STATUS_FILTER, DEFAULT_VALUE_KEY } from '../consts';
import { transformCompanies, transformUsers, transformRoles } from '../helpers/formatters';
import { sortUsersAlphabetically } from '../helpers/utils';
/**
 * A Dropdown component to multiselect items from a dropdown that
 * contains Users, Roles and Companies grouped by category
 */
const useAssigneePicker = function () {
  let {
    companyOptionValue = DEFAULT_VALUE_KEY,
    roleOptionValue = DEFAULT_VALUE_KEY,
    userOptionValue = DEFAULT_VALUE_KEY,
    companyOptionLabel,
    roleOptionLabel,
    userOptionLabel,
    userOptionSubtextRenderer,
    optionsOrder = DEFAULT_OPTIONS_ORDER,
    statusFilters = {},
    optionsFilter = {},
    additionalCompanyDataFields,
    additionalUserDataFields,
    additionalRoleDataFields,
    customUsers,
    customRoles,
    customCompanies,
    postFormatters,
    hideDeletedEntitiesInMenu = {}
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  const {
    T
  } = useLocale(_ref => {
    let {
      locale
    } = _ref;
    return require("../i18n/".concat(locale, ".json"));
  });
  const [groupedItems, setGroupedItems] = useState([]);
  const [roleCountById, setRoleCountById] = useState({});
  const alloyUsers = useUsers();
  const users = customUsers || alloyUsers;
  const {
    users: statusFiltersUsers = DEFAULT_USERS_STATUS_FILTER
  } = statusFilters;
  const includeDeletedUsers = useMemo(() => statusFiltersUsers.includes('deleted') && !customUsers, [customUsers, statusFiltersUsers]);
  const alloyDeletedUsers = useDeletedUsers({
    disable: !includeDeletedUsers
  });
  const alloyRoles = useRoles();
  const roles = customRoles || alloyRoles;
  const alloyCompanies = useCompanies();
  const companies = customCompanies || alloyCompanies;
  const usersSize = includeDeletedUsers ? (users === null || users === void 0 ? void 0 : users.data.length) + (alloyDeletedUsers === null || alloyDeletedUsers === void 0 ? void 0 : alloyDeletedUsers.data.length) : users === null || users === void 0 ? void 0 : users.data.length;
  const rolesSize = roles === null || roles === void 0 ? void 0 : roles.data.length;
  const companiesSize = companies === null || companies === void 0 ? void 0 : companies.data.length;
  const isLoading = (includeDeletedUsers ? [users, roles, companies, alloyDeletedUsers] : [users, roles, companies]).some(entity => entity === null || entity === void 0 ? void 0 : entity.loading);
  const {
    formatDisplayUser
  } = useFormattedUser();
  const companyOptionsFilter = optionsFilter[ASSIGNEE_PICKER_COMPANY];
  const roleOptionsFilter = optionsFilter[ASSIGNEE_PICKER_ROLE];
  const userOptionsFilter = optionsFilter[ASSIGNEE_PICKER_USER];
  const getGroupedItems = useCallback(() => {
    const [companyIdToNameMap, companyOptions] = transformCompanies(companies.data, companyOptionValue, T, statusFilters.companies, companyOptionsFilter, additionalCompanyDataFields, postFormatters === null || postFormatters === void 0 ? void 0 : postFormatters.postTransformCompanies);
    const [rolesMemberCount, userOptions] = transformUsers(includeDeletedUsers ? sortUsersAlphabetically([...users.data, ...alloyDeletedUsers.data]) : users.data, userOptionValue, formatDisplayUser, statusFiltersUsers, userOptionsFilter, additionalUserDataFields, companyIdToNameMap, userOptionSubtextRenderer, postFormatters === null || postFormatters === void 0 ? void 0 : postFormatters.postTransformUsers);
    const roleItems = transformRoles(roles.data, roleOptionValue, T, statusFilters.roles, roleOptionsFilter, additionalRoleDataFields, rolesMemberCount, postFormatters === null || postFormatters === void 0 ? void 0 : postFormatters.postTransformRoles);
    const allOptions = {
      company: [{
        label: companyOptionLabel || T.translate('COMPANIES'),
        items: companyOptions,
        group: ASSIGNEE_PICKER_COMPANY
      }],
      role: [{
        label: roleOptionLabel || T.translate('ROLES'),
        items: roleItems,
        group: ASSIGNEE_PICKER_ROLE
      }],
      user: [{
        label: userOptionLabel || T.translate('MEMBERS'),
        items: userOptions,
        group: ASSIGNEE_PICKER_USER
      }]
    };
    return {
      groupedItems: optionsOrder.reduce((acc, category) => [...acc, ...allOptions[category]], []),
      rolesMemberCount
    };
  }, [companies.data, companyOptionValue, T, statusFilters.companies, statusFilters.roles, companyOptionsFilter, additionalCompanyDataFields, postFormatters === null || postFormatters === void 0 ? void 0 : postFormatters.postTransformCompanies, postFormatters === null || postFormatters === void 0 ? void 0 : postFormatters.postTransformUsers, postFormatters === null || postFormatters === void 0 ? void 0 : postFormatters.postTransformRoles, includeDeletedUsers, users.data, alloyDeletedUsers.data, userOptionValue, formatDisplayUser, statusFiltersUsers, userOptionsFilter, additionalUserDataFields, userOptionSubtextRenderer, roles.data, roleOptionValue, roleOptionsFilter, additionalRoleDataFields, companyOptionLabel, roleOptionLabel, userOptionLabel, optionsOrder]);
  useEffect(() => {
    // We reset the grouped items array when the session storage is cleared and
    // isLoading changes from false -> true
    const {
      groupedItems,
      rolesMemberCount
    } = getGroupedItems();
    setGroupedItems(isLoading ? [] : groupedItems);
    setRoleCountById(rolesMemberCount);
  }, [usersSize, rolesSize, companiesSize, isLoading, getGroupedItems]);
  const hiddenMenuItemsIdsSet = useMemo(() => {
    const hiddenMenuUsersMap = new Set();
    if (!includeDeletedUsers || !hideDeletedEntitiesInMenu.users) {
      return hiddenMenuUsersMap;
    }
    alloyDeletedUsers.data.forEach(user => {
      hiddenMenuUsersMap.add(user[userOptionValue]);
    });
    return hiddenMenuUsersMap;
  }, [alloyDeletedUsers.data, hideDeletedEntitiesInMenu, includeDeletedUsers, userOptionValue]);
  return {
    groupedItems,
    hiddenMenuItemsIdsSet,
    isLoading,
    roleCountById
  };
};
useAssigneePicker.propTypes = {
  /**
   * order which the categories are shown in the dropdown
   */
  optionsOrder: PropTypes.arrayOf(PropTypes.oneOf(DEFAULT_OPTIONS_ORDER)),
  /**
   * filters used for filtering options by status
   */
  statusFilters: PropTypes.shape({
    companies: PropTypes.arrayOf(PropTypes.oneOf(['active', 'deleted'])),
    roles: PropTypes.arrayOf(PropTypes.oneOf(['active', 'inactive'])),
    users: PropTypes.arrayOf(PropTypes.oneOf(['active', 'pending', 'disabled', 'deleted']))
  }),
  /**
   * filters used for filtering options in the dropdown
   */
  optionsFilter: PropTypes.shape({
    company: PropTypes.func,
    role: PropTypes.func,
    user: PropTypes.func
  }),
  /**
   * Additional data fields to include for companies (accountId, updatedAt, etc.)
   */
  additionalCompanyDataFields: PropTypes.array,
  /**
   * Additional data fields to include for users (imageUrl, analyticsId, etc.)
   */
  additionalUserDataFields: PropTypes.array,
  /**
   * Additional data fields to include for roles (templateId, createdAt, etc.)
   */
  additionalRoleDataFields: PropTypes.array,
  /**
   * company attribute used as value in company options (memberGroupId, id, etc.)
   */
  companyOptionValue: PropTypes.string,
  /**
   * user attribute used as value in user options (autodeskId, id, etc.)
   */
  userOptionValue: PropTypes.string,
  /**
   * role attribute used as value in role options (memberGroupId, id, etc.)
   */
  roleOptionValue: PropTypes.string,
  /**
   * company option label
   */
  companyOptionLabel: PropTypes.string,
  /**
   * user option label
   */
  userOptionLabel: PropTypes.string,
  /**
   * role option label
   */
  roleOptionLabel: PropTypes.string,
  /**
   * user option subtext renderer
   */
  userOptionSubtextRenderer: PropTypes.func
};
export default useAssigneePicker;
//# sourceMappingURL=useAssigneePicker.js.map